import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';
import cn from 'classnames';
import { RouteComponentProps, Redirect } from '@reach/router';
import { Button, HeaderText, Text } from '@el8/vital';
import { Card } from 'components';
import { getAppointment } from 'modules/appointment';
import { getBookingInfo } from 'modules/booking';
import { getPhysician } from 'modules/physician';
import { getChart } from 'modules/chart';
import { getAppointmentType } from 'modules/appointmentType';
import Content from 'views/booking/common/Content/Content';
import { getPractice } from 'modules/practice';
import { formatPhone } from 'utils';
import { formatPhysicianName } from 'utils/physician';
import { getPracticePhone } from 'utils/practice';
import { humanizeDate, isDateTimeWithinHours } from 'utils/dates';
import { getFullNameFromPatientChart } from 'utils/patient';

import { State, PhysicianInfo, PatientChartInfo, PracticeInfo, ServiceLocationInfo } from 'types';
import { AppointmentType, Appointment } from 'EntityTypes';

import styles from './Success.less';
import SuccessIcon from './SuccessIcon';
import Burst from './Burst';
import { getServiceLocation } from '../../../modules/serviceLocations';

type OwnProps = {
  location?: {
    state: {
      appointmentId: number;
      isNewAccount: boolean;
    };
  } & Location;
  practiceId?: number;
} & RouteComponentProps;

type StateProps = {
  isNewAccount?: boolean;
  patient?: PatientChartInfo;
  physician?: PhysicianInfo;
  practice?: PracticeInfo;
  appointmentType?: AppointmentType;
  appointment?: Appointment;
  serviceLocation?: ServiceLocationInfo;
  timezone?: string;
};

type Props = OwnProps & StateProps;

const mapStateToProps = (state: State, { location, practiceId }: OwnProps): StateProps => {
  const appointment = getAppointment(state, {
    appointmentId: location.state ? location.state.appointmentId : undefined,
  });

  if (!appointment) {
    return {};
  }

  const { apptType, physician, patient, serviceLocationId } = appointment;
  const appointmentType = getAppointmentType(state, { appointmentTypeId: apptType });
  const { timezone } = getBookingInfo(state);
  return {
    appointment,
    appointmentType,
    isNewAccount: !state.session.activeUserId,
    patient: getChart(state, { chartId: patient }),
    physician: getPhysician(state, { physicianId: physician }),
    practice: getPractice(state, { practiceId }),
    serviceLocation: getServiceLocation(state, serviceLocationId),
    timezone,
  };
};

const httpRegexp = /^https?:\/\//;

const Success: React.FC<Props> = ({
  isNewAccount,
  patient,
  practiceId,
  practice,
  physician,
  appointmentType,
  appointment,
  serviceLocation,
  timezone,
}) => {
  const practicePhone = useMemo(() => getPracticePhone(practice), [practice]);
  const appointmentLocationName = serviceLocation ? serviceLocation.name : practice.name;
  const appointmentLocationAddress = serviceLocation
    ? serviceLocation.fullAddress
    : practice.settings.practiceAddress;
  const appointmentLocationPhone = serviceLocation
    ? formatPhone(serviceLocation.phone)
    : practicePhone;
  if (!(physician && appointment?.apptTime && appointmentType.name)) {
    return <Redirect noThrow to={`book/${practiceId}/`} />;
  }

  if (!patient) {
    return <Redirect noThrow to={`book/${practiceId}/account`} />;
  }

  const { apptTime, patientFormHoursPrior, patientFormsAnonymousUrl } = appointment;
  const shouldShowPatientFormsLink =
    (patientFormHoursPrior === 0 || isDateTimeWithinHours(apptTime, patientFormHoursPrior)) &&
    patientFormsAnonymousUrl;

  const tz = window.el8Globals.FEATURES.SchedulingTimeZone ? timezone : 'local';

  return (
    <Content>
      <Card className={styles.appointmentSummary}>
        <Burst className={styles.burst} />
        <SuccessIcon />
        <HeaderText
          className={cn(styles.thin, styles.fadeIn)}
          style={{ animationDelay: '0.12ms' }}
          legacyVariant="legacyHeaderText"
          tag="h1"
        >
          Appointment Booked for <strong>{getFullNameFromPatientChart(patient)}</strong>!
        </HeaderText>
        <HeaderText
          align="center"
          tag="h1"
          className={cn(styles.thin, styles.fadeIn)}
          style={{ animationDelay: '0.2s' }}
          legacyVariant="legacyHeaderText"
          sizing="heading-sm"
        >
          {`${appointmentType.name} with ${formatPhysicianName(physician)}`}
        </HeaderText>
        <HeaderText
          align="center"
          className={cn(styles.date, styles.fadeIn)}
          style={{ animationDelay: '0.16s' }}
          legacyVariant="legacyTitleText"
          sizing="heading-lg"
          tag="h1"
        >
          {`${humanizeDate(apptTime)} ${DateTime.fromISO(apptTime)
            .setZone(tz)
            .toFormat('@ h:mm a ZZZZ')}`}
        </HeaderText>
        <HeaderText
          align="center"
          className={cn(styles.thin, styles.fadeIn)}
          style={{ animationDelay: '0.20s' }}
          legacyVariant="legacyHeaderText"
          tag="h1"
        >
          {appointmentLocationName}
        </HeaderText>
        <Text
          align="center"
          appearance="system"
          className={styles.fadeIn}
          style={{ animationDelay: '0.20s' }}
          tag="div"
        >
          {appointmentLocationAddress}
        </Text>
        {practicePhone && (
          <Text
            align="center"
            appearance="system"
            className={styles.fadeIn}
            style={{ animationDelay: '0.20s' }}
            tag="div"
          >
            {appointmentLocationPhone}
          </Text>
        )}
        {patient.email &&
          (isNewAccount ? (
            <Text
              className={cn(styles.text, styles.fadeIn)}
              appearance="system"
              align="center"
              style={{ animationDelay: '0.24s' }}
              tag="div"
            >
              We sent a confirmation with appointment details as well as an invite to your patient
              account to <strong>{patient.email}</strong>
            </Text>
          ) : (
            <Text
              className={cn(styles.text, styles.fadeIn)}
              appearance="system"
              align="center"
              style={{ animationDelay: '0.24s' }}
              tag="div"
            >
              We sent a confirmation with appointment details to <strong>{patient.email}</strong>
            </Text>
          ))}
        <Text
          align="center"
          appearance="system"
          className={cn(styles.cancellationPolicy, styles.fadeIn)}
          tag="div"
          style={{ animationDelay: '0.28s' }}
        >
          {practice.settings.cancellationPolicy}
        </Text>
      </Card>
      {shouldShowPatientFormsLink && (
        <Card className={styles.fadeIn} style={{ animationDelay: '0.32s' }}>
          <HeaderText
            align="center"
            tag="h3"
            legacyVariant="legacyHeaderText"
            sizing="subheading-sm"
          >
            Next, complete these forms to save time in the waiting room.
          </HeaderText>
          <Button
            kind="cta"
            isAnchor
            href={patientFormsAnonymousUrl}
            target="_blank"
            text="Complete forms"
            style={{ marginTop: 20 }}
          />
        </Card>
      )}
      {!!appointmentType.forms.length && (
        <Card
          alignContent="stretch"
          className={styles.fadeIn}
          style={{ animationDelay: shouldShowPatientFormsLink ? '0.36s' : '0.32s' }}
        >
          <HeaderText
            align="center"
            tag="h3"
            legacyVariant="legacyHeaderText"
            sizing="subheading-sm"
          >
            Resource Links from the Practice
          </HeaderText>
          <ol className={styles.formsList}>
            {appointmentType.forms.map(
              (form): JSX.Element => (
                <li className={styles.formItem} key={form}>
                  <a
                    className={styles.formsLink}
                    href={httpRegexp.test(form) ? form : `http://${form}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {form.replace(httpRegexp, '')}
                  </a>
                </li>
              ),
            )}
          </ol>
        </Card>
      )}
    </Content>
  );
};

export default connect(mapStateToProps)(Success);
